import { useMemo } from 'react';
import { Box, CardContent, Stack, lighten } from '@mui/material';

import { Card, Typography, IconButton, EditOutlinedIcon } from '@occc/ui-design-system';
import { GoalProgressChartData, Retailer_Network_Performance_Period } from '@occc/graphql';

import { Chart } from './chart.component';
import { ChartName } from './retailer-network-performance-chart.types';

import { formatPerformanceNumber } from '../../retailer-network-performance.utils';
import { RetailerNetworkPerformanceScope } from '../../retailer-network-performance.types';

type RetailerNetworkPerformanceChartDataProps = {
  chartTitle: string;
  chartData: GoalProgressChartData[];
  progress: number;
  targetGoal: number;
  period: Retailer_Network_Performance_Period;
  chartName: ChartName;
  isSpecificGoalSet?: boolean;
  openSpecificGoalDialog?: () => void;
  scope: RetailerNetworkPerformanceScope;
  isUserPermittedToEditSpecificGoals: boolean;
};

export const RetailerNetworkPerformanceChart: React.FC<
  RetailerNetworkPerformanceChartDataProps
> = ({
  chartTitle,
  chartData,
  progress,
  targetGoal,
  period,
  chartName,
  isSpecificGoalSet,
  openSpecificGoalDialog,
  scope,
  isUserPermittedToEditSpecificGoals,
}) => {
  const progressFormatted = useMemo(() => formatPerformanceNumber(progress), [progress]);
  const targetGoalFormatted = useMemo(() => formatPerformanceNumber(targetGoal), [targetGoal]);
  const isEditSpecificGoalsButtonVisible =
    scope === 'location' && isUserPermittedToEditSpecificGoals;

  return (
    <Card variant='outlined' elevation={0} sx={{ overflow: 'visible' }}>
      <CardContent sx={{ p: 2, '&.MuiCardContent-root:last-child': { pb: 0 } }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Stack>
            <Typography variant='body1' gutterBottom>
              {chartTitle}
            </Typography>
            <div>
              <Typography variant='h4' component='span'>
                {progressFormatted}
              </Typography>
              <Typography variant='body2' component='span' color='textSecondary'>
                {`/${targetGoalFormatted} goal ${isSpecificGoalSet ? '• Site specific' : ''}`}
              </Typography>
            </div>
          </Stack>

          {isEditSpecificGoalsButtonVisible && (
            <IconButton
              size='small'
              color='primary'
              sx={({ palette }) => ({
                background: lighten(palette.primary.light, 0.8),
                p: 1,
                borderRadius: 2,
              })}
              onClick={openSpecificGoalDialog}
            >
              <EditOutlinedIcon color='primary' />
            </IconButton>
          )}
        </Box>
        <Chart
          chartData={chartData}
          targetGoal={targetGoal}
          period={period}
          chartName={chartName}
        />
      </CardContent>
    </Card>
  );
};
